import { Alert, Button, Card, Col, DatePicker, Row, Space, Statistic, Table, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type PulseQueue = {
  key: string;
  title: string;
  count: number;
  severity: 'info' | 'warning' | 'critical';
  path: string;
  detail?: string | null;
};

type PulsePayload = {
  as_of: string;
  summary: {
    attention_items: number;
    queue_count: number;
    critical_queues: number;
  };
  queues: PulseQueue[];
  notes?: string[];
};

function severityColor(severity: PulseQueue['severity']): string {
  if (severity === 'critical') return 'red';
  if (severity === 'warning') return 'orange';
  return 'default';
}

export default function PrincipalPulsePage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [asOf, setAsOf] = useState<Dayjs>(() => dayjs());

  const query = useQuery({
    queryKey: ['education-principal-pulse', asOf.format('YYYY-MM-DD')],
    queryFn: async () =>
      (
        await api.get<PulsePayload>('/education/pulse', {
          params: { as_of: asOf.format('YYYY-MM-DD') },
        })
      ).data,
  });

  const pulse = query.data;

  return (
    <EducationPageChrome
      title={t('principalPulse')}
      description={t('principalPulseIntro')}
      tags={pulse ? <Tag>{pulse.as_of}</Tag> : undefined}
      extra={
        <Space wrap>
          <DatePicker value={asOf} allowClear={false} onChange={(v) => v && setAsOf(v)} />
          <Button onClick={() => navigate('/education')}>{t('backToHub', { defaultValue: 'Back' })}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('principalPulseScopeNote')} />

        <Row gutter={[16, 16]}>
          <Col xs={12} md={8}>
            <Card size="small" loading={query.isLoading}>
              <Statistic title={t('pulseAttentionItems')} value={pulse?.summary.attention_items ?? 0} />
            </Card>
          </Col>
          <Col xs={12} md={8}>
            <Card size="small" loading={query.isLoading}>
              <Statistic title={t('pulseQueues')} value={pulse?.summary.queue_count ?? 0} />
            </Card>
          </Col>
          <Col xs={12} md={8}>
            <Card size="small" loading={query.isLoading}>
              <Statistic title={t('pulseCriticalQueues')} value={pulse?.summary.critical_queues ?? 0} />
            </Card>
          </Col>
        </Row>

        <Card size="small" title={t('pulseRoleQueues')}>
          <Table
            rowKey="key"
            size="small"
            loading={query.isLoading}
            pagination={false}
            dataSource={pulse?.queues ?? []}
            onRow={(row) => ({
              onClick: () => navigate(row.path),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('queue'), dataIndex: 'title' },
              {
                title: t('count'),
                dataIndex: 'count',
                width: 90,
              },
              {
                title: t('severity'),
                dataIndex: 'severity',
                width: 110,
                render: (v: PulseQueue['severity']) => <Tag color={severityColor(v)}>{v}</Tag>,
              },
              {
                title: t('detail'),
                dataIndex: 'detail',
                ellipsis: true,
                render: (v?: string | null) => v || '—',
              },
              {
                title: t('actions'),
                width: 100,
                render: (_: unknown, row: PulseQueue) => (
                  <Button
                    size="small"
                    type="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(row.path);
                    }}
                  >
                    {t('open')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
